import { message } from "antd";
import React, { useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useMutation, useQuery } from "react-query";
import { getAllCategories, postFacilityService } from "../../../helpers/api";
import _ from "lodash";
import { RequiredIndicator } from "../../../components";
import smallTalk from "smalltalk";
import { Alert, Modal, Switch } from "@mantine/core";
import { NewCategoryForm } from "../../../components/shared/new-category";
import { CATEGORY_TYPES } from "../../../helpers/config";

const MARKUP_TYPES = { fixed: "fixed", percent: "percent" };

export default function NewServiceForm({ onSuccess }) {
  const { handleSubmit, register, reset, control } = useForm();
  const [isOnline, setIsOnline] = useState(false);
  const [markupType, setMarkupType] = useState(MARKUP_TYPES.fixed);
  const watchedBase = useWatch({ control, name: "baseRate", defaultValue: "" });
  const watchedMarkup = useWatch({ control, name: "markup", defaultValue: "" });

  const { data: categories = [], refetch } = useQuery({
    queryFn: () => getAllCategories("service"),
    queryKey: ["service-categories"],
  });

  const [modal, setModal] = useState({
    isOpen: false,
    title: "",
    content: null,
    zIndex: 1000,
  });

  const { mutateAsync, isLoading } = useMutation(
    (data) => postFacilityService(data),
    {
      onSuccess: (data, variables, context) => {
        if (data.status === 201) {
          message.success(data.data.message);
          reset();
          onSuccess();
          return;
        }

        throw data;
      },

      onError: (error, variables, context) => {
        const err = error.response?.data?.message;

        if (_.isArray(err)) {
          err.map((err) => message.error(err.message));
        } else {
          message.error(err);
        }
      },
      retry: true,
    }
  );

  const postSubmit = (data) => {
    smallTalk
      .confirm("Add Service", "You are about to add a service. Continue?", {
        buttons: {
          ok: "YES",
          cancel: "NO",
        },
      })
      .then(() =>
        mutateAsync({
          ...data,
          isOnline,
          markup: parseFloat(data.markup) || 0,
          markupType,
        })
      );
  };

  return (
    <>
      <Modal
        onClose={() => setModal({ ...modal, isOpen: false })}
        opened={modal.isOpen}
        title={modal.title}
        zIndex={modal.zIndex}>
        {modal.content}
      </Modal>
      <form onSubmit={handleSubmit(postSubmit)}>
        <div className="row g-2">
          <div className="col" style={{ flex: "0 0 30%", maxWidth: "30%" }}>
            <div className="field">
              <label htmlFor="serviceCode">Service Code</label>
              <input
                type="text"
                id="serviceCode"
                autoFocus
                {...register("serviceCode")}
                className="input w-100"
                placeholder="unique identification service code"
              />
              {/* <small>leave blank to auto-generate</small> */}
            </div>
          </div>
          <div className="col" style={{ flex: "0 0 70%", maxWidth: "70%" }}>
            <div className="field">
              <label htmlFor="serviceName">
                Service Name
                <RequiredIndicator />
              </label>
              <input
                type="text"
                id="serviceName"
                {...register("serviceName", { required: true })}
                className="input w-100"
                placeholder="official service name"
              />
            </div>
          </div>
        </div>
        <div className="row g-2 my-2">
          <div className="col" style={{ flex: "0 0 40%", maxWidth: "40%" }}>
            <div className="field">
              <label htmlFor="baseRate">
                Base price
                <RequiredIndicator />
              </label>
              <input
                type="number"
                step="0.01"
                id="baseRate"
                {...register("baseRate", { required: true })}
                className="input w-100"
                placeholder="service base rate before markup"
              />
            </div>
          </div>
          <div className="col" style={{ flex: "0 0 50%", maxWidth: "50%" }}>
            <div className="field">
              <label htmlFor="markup">Markup</label>
              <div className="d-flex align-items-center gap-2">
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  id="markup"
                  {...register("markup")}
                  className="input"
                  style={{ width: "120px" }}
                  placeholder={markupType === MARKUP_TYPES.percent ? "e.g. 10" : "e.g. 5.00"}
                />
                {/* <span className="text-secondary">as</span> */}
                <button
                  type="button"
                  className={`px-2 button app-btn ${markupType === MARKUP_TYPES.fixed ? "btn-prim" : "is-ghost"}`}
                  onClick={() => setMarkupType(MARKUP_TYPES.fixed)}>
                  Fixed
                </button>
                <button
                  type="button"
                  className={`px-3 button app-btn ${markupType === MARKUP_TYPES.percent ? "btn-prim" : "is-ghost"}`}
                  onClick={() => setMarkupType(MARKUP_TYPES.percent)}>
                  %
                </button>
              </div>
              <small>
                {markupType === MARKUP_TYPES.percent
                  ? "Percentage added to base price"
                  : "Fixed amount added to base price"}
              </small>

            </div>

          </div>
        </div>
        <div className="field">
          <label htmlFor="categories">
            Category
            <RequiredIndicator />
          </label>
          <select
            size="large"
            name="categories"
            id="categories"
            className="d-block w-100 input"
            placeholder="select expense category"
            {...register("categoryId", { required: true })}>
            <option value="" selected disabled>
              select
            </option>
            {categories.map((cat) => (
              <option value={cat.id} id={cat.id}>
                {cat.title}
              </option>
            ))}
          </select>
          <p
            onClick={() =>
              setModal({
                title: "Add Category",
                isOpen: true,
                content: (
                  <NewCategoryForm
                    onSuccess={refetch}
                    type={
                      CATEGORY_TYPES.find((cat) => cat.value === "service")
                        .value
                    }
                  />
                ),
                zIndex: 1200,
              })
            }
            className="text-secondary hover-hand d-inline-block p-1 mb-0">
            <span className="bi bi-plus-circle me-2"></span>
            add category
          </p>
        </div>
        <div className="field">
          <label htmlFor="description">Description</label>
          <textarea
            type="text"
            step="0.01"
            rows="3"
            id="description"
            {...register("description")}
            className="textarea w-100"
            placeholder="service description here"
          />
        </div>

        <div className="field">
          {(() => {
            const raw = parseFloat(watchedBase) || 0;
            const markupVal = parseFloat(watchedMarkup) || 0;
            const total =
              markupType === MARKUP_TYPES.percent
                ? raw * (1 + markupVal / 100)
                : raw + markupVal;
            if (raw > 0 || markupVal > 0) {
              return (
                <Alert color="green" variant="light" className="mt-2">
                  <span className="fs-5 fw-semibold">
                    Total price: GHS {total.toFixed(2)}
                  </span>
                </Alert>
              );
            }
            return null;
          })()}
        </div>
        <div className="field">
          <Switch
            checked={isOnline}
            onChange={() => setIsOnline(!isOnline)}
            color="teal"
            size="md"
            label={`Enable Online Booking (${isOnline ? "YES" : "NO"})`}
            onLabel="YES"
            offLabel="NO"
          />
        </div>
        {isOnline && (
          <div className="field">
            <label htmlFor="onlineRate">
              Online Price
              <RequiredIndicator />
            </label>
            <input
              type="number"
              step="0.01"
              id="onlineRate"
              {...register("onlineRate", { required: isOnline })}
              className="input w-100"
              placeholder="service online rate"
            />
          </div>
        )}

        <button
          className={`button h6 btn-prim mt-3 ${isLoading && " is-loading"}`}
          style={{ minWidth: "8rem" }}
          type="submit">
          <span className="bi bi-save me-2"></span>
          Save
        </button>
      </form>
    </>
  );
}
